// src/components/Acetileno/AcetilenoList.tsx
import React, {
  useState,
  useEffect,
  useRef,
  ElementRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import DxDataGrid, {
  Column,
  Paging,
  Pager,
  ColumnFixing,
  Button,
  FilterRow,
  HeaderFilter,
  Lookup,
  SearchPanel,
} from "devextreme-react/data-grid";
import { Button as DxButton } from "devextreme-react/button";
import { LoadIndicator } from "devextreme-react";
import { custom } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import ExcelJS from "exceljs";
import saveAs from "file-saver";

import { Acetylene } from "../../dtos/Acetylene";
import { acetilenoService } from "../../services/acetilenoService";
import {
  GridContainer,
  HeaderContainer,
  PageContainer,
  Title,
  AddUserButton,
  DevExtremeModalGlobalStyles,
  FilterBar,
  Field,
  Label,
  StyledSelect,
  StyledInput,
  DateInput,
  ActionsRow,
} from "./Acetileno.styles";

import defaultHeaderImage from "../../assets/img/infra-logo.svg";
import { catalogoService } from "../../services/catalogoService";

const deleteIcon = "trash";

// Filtros para el Lookup del grid
const statusFilters = [
  { value: 0, text: "Pendiente" },
  { value: 1, text: "Consolidado" },
  { value: 2, text: "Reportado" },
  { value: 3, text: "Descartado" },
  { value: 4, text: "Reportado" },
];

// ====== Códigos de estatus (back) ======
const STATUS_PENDIENTE = 0;
const STATUS_CONSOLIDADO = 1;
const STATUS_REPORTADO = 4; // también puede venir 2
const STATUS_REPORTADO_ALT = 2;
const STATUS_DESCARTADO = 3;

// ===== helpers de fecha (YYYY-MM-DD) =====
const toYMD = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const todayYMD = () => toYMD(new Date());
const subDaysYMD = (days: number) =>
  toYMD(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

const AcetilenoList: React.FC = () => {
  const navigate = useNavigate();
  const dataGridRef = useRef<ElementRef<typeof DxDataGrid>>(null);

  const [acetylene, setAcetylene] = useState<Acetylene[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // ---- estado de vista (botones) ----
  const [viewStatus, setViewStatus] = useState<number | null>(null);

  // ---- estado de "reportar todos" / progreso ----
  const [isReportingAll, setIsReportingAll] = useState<boolean>(false);
  const [reportingDone, setReportingDone] = useState<number>(0);
  const [reportingTotal, setReportingTotal] = useState<number>(0);

  // ------- estado filtros -------
  const [sucursales, setSucursales] = useState<any[]>([]);
  const [filterSucursal, setFilterSucursal] = useState<number | "">("");
  const [filterOperador, setFilterOperador] = useState<string>("");
  const [filterStart, setFilterStart] = useState<string>(subDaysYMD(7));
  const [filterEnd, setFilterEnd] = useState<string>(todayYMD());

  // ====== cargar catálogo de sucursales ======
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const resp = await catalogoService.getBranch();
        setSucursales(resp?.data || []);
      } catch (err: any) {
        notify(
          { message: err?.message || "Error al cargar sucursales" },
          "error",
          3000
        );
      }
    };
    loadBranches();
  }, []);

  // ====== Carga inicial ======
  useEffect(() => {
    const initialSearch = async () => {
      const stored = localStorage.getItem("userData");
      setLoading(true);
      setError(null);
      try {
        if (!stored) return;
        const parsed = JSON.parse(stored);
        const sucursalId = parsed?.user?.sucursalId ?? 0;
        setFilterSucursal(sucursalId);

        const resp = await acetilenoService.getAcetilenoConsolidadoByFilters({
          sucursalId,
          fechainicio: filterStart,
          fechaFin: filterEnd,
          usuario: "",
        });

        if (resp?.error) {
          const msg = String(resp.error.message || "").toLowerCase();
          if (msg.includes("no se encontraron registros")) {
            setAcetylene([]);
          } else {
            throw new Error(resp.error.message);
          }
        } else {
          setAcetylene(resp?.data || []);
        }
      } catch (e: any) {
        setError(e.message || "Error desconocido al cargar datos");
        setAcetylene([]);
      } finally {
        setLoading(false);
      }
    };
    initialSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Dataset para el grid (según pestaña) ----
  const dataForGrid = useMemo(() => {
    if (viewStatus === null) {
      // TODOS: excluir DESCARTADOS
      return acetylene.filter(
        (x) => Number(x.statusAcetileno) !== STATUS_DESCARTADO
      );
    }

    if (viewStatus === STATUS_PENDIENTE) {
      return acetylene.filter(
        (x) => Number(x.statusAcetileno) === STATUS_PENDIENTE
      );
    }

    if (viewStatus === STATUS_REPORTADO) {
      return acetylene.filter((x) =>
        [STATUS_REPORTADO, STATUS_REPORTADO_ALT].includes(
          Number(x.statusAcetileno)
        )
      );
    }

    // Extra (si agregas más tabs en el futuro)
    return acetylene.filter((x) => Number(x.statusAcetileno) === viewStatus);
  }, [acetylene, viewStatus]);

  // --- cuando cambias de pestaña o cambia el dataset, regresa a la página 0
  useEffect(() => {
    const instance = dataGridRef.current?.instance();
    if (instance) {
      instance.pageIndex(0);
    }
  }, [viewStatus, acetylene, dataForGrid]);

  const fmt = (d?: string) => (d && d.length >= 10 ? d.slice(0, 10) : d || "");

  // ---- Buscar con filtros ----
  const handleSearch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const resp = await acetilenoService.getAcetilenoConsolidadoByFilters({
        sucursalId: filterSucursal === "" ? 0 : Number(filterSucursal),
        fechainicio: fmt(filterStart),
        fechaFin: fmt(filterEnd),
        usuario: filterOperador?.trim() || "",
      });

      if (resp?.error) {
        const msg = String(resp.error.message || "").toLowerCase();
        if (msg.includes("no se encontraron registros")) {
          setAcetylene([]);
          notify({
            message: "Sin resultados con los filtros aplicados.",
            type: "info",
            displayTime: 2500,
          });
        } else {
          throw new Error(resp.error.message);
        }
      } else {
        const data = (resp?.data || []) as Acetylene[];
        setAcetylene(data);
      }
    } catch (e: any) {
      setError(e.message);
      setAcetylene([]);
    } finally {
      setLoading(false);
      // Asegura reiniciar a la primera página tras una búsqueda
      setTimeout(() => {
        const instance = dataGridRef.current?.instance();
        instance?.pageIndex(0);
      }, 0);
    }
  }, [filterSucursal, filterStart, filterEnd, filterOperador]);

  // ==== Texto estatus ====
  const getStatusText = (status: number | undefined) => {
    switch (status) {
      case STATUS_PENDIENTE:
        return "Pendiente";
      case STATUS_CONSOLIDADO:
        return "Consolidado";
      case STATUS_REPORTADO_ALT:
      case STATUS_REPORTADO:
        return "Reportado";
      case STATUS_DESCARTADO:
        return "Descartado";
      default:
        return "Desconocido";
    }
  };

  const getDateTimeString = (fecha: string, hora?: string) => {
    if (!fecha) return "";

    // 1. Solo la parte de fecha (antes de la "T" si viene en ISO)
    const soloFecha = fecha.split("T")[0]; // "2025-10-28"

    // 2. Parse manual YYYY-MM-DD
    const [yyyy, mm, dd] = soloFecha.split("-");

    // 3. Formato DD/MM/YYYY
    const fechaFormateada =
      dd && mm && yyyy ? `${dd}/${mm}/${yyyy}` : soloFecha;

    // 4. Hora, sin tocarla
    const horaFinal = hora && hora.trim() !== "" ? hora : "00:00:00";

    return `${fechaFormateada} ${horaFinal}`;
  };

  const loadLogoAsPngBase64 = async (url: string, w: number, h: number) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    return new Promise<string>((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas no soportado"));
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png").split(",")[1]);
      };
      img.onerror = () => reject(new Error("No se pudo cargar la imagen"));
      img.src = url;
    });
  };

  // ==== Exportar Excel (TODOS los registros filtrados, no solo la página actual) ====
  const handleExportExcel = async () => {
    try {
      // usamos TODOS los datos visibles lógicamente en la UI, NO solo la página que se ve
      const dataToExport = dataForGrid; // <-- dataset filtrado completo

      if (!dataToExport.length) {
        notify({
          message: "No hay datos para exportar.",
          type: "warning",
          displayTime: 3000,
        });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Acetileno");

      const imgBase64 = await loadLogoAsPngBase64(defaultHeaderImage, 150, 32);
      const logoId = workbook.addImage({ base64: imgBase64, extension: "png" });
      worksheet.addImage(logoId, "A2:B3");

      worksheet.mergeCells("A4:H4");
      worksheet.getCell("A4").value =
        "Reporte de Acetonado de Cilindros de Acetileno";
      worksheet.getCell("A4").font = { name: "Calibri", size: 14, bold: true };
      worksheet.getCell("A4").alignment = { horizontal: "center" };

      worksheet.columns = [
        { key: "index", width: 5 },
        { key: "sucursal", width: 20 },
        { key: "nombreUsuario", width: 30 },
        { key: "fechaHora", width: 22 },
        { key: "celda", width: 10 },
        { key: "tipoEnvase" },
        { key: "cantidad", width: 20, style: { numFmt: "#,##0.00" } },
        { key: "statusAcetileno", width: 15 },
      ];

      const headerRow = worksheet.getRow(6);
      headerRow.values = [
        "#",
        "Sucursal",
        "Operador",
        "Fecha y Hora",
        "Báscula",
        "Tipo Envase",
        "Cantidad",
        "Status",
      ];

      const rows = dataToExport.map((item, i) => ({
        index: i + 1,
        sucursal: item.sucursal ?? "",
        nombreUsuario: item.nombreUsuario ?? "",
        fechaHora: getDateTimeString(
          item.fecha?.toString() ?? "",
          item.hora?.toString()
        ),
        celda: item.celda ?? "",
        tipoEnvase: item.tipoEnvase ?? "",
        cantidad: item.cantidad ?? 0,
        statusAcetileno: getStatusText(Number(item.statusAcetileno)),
      }));
      worksheet.addRows(rows);

      const total = dataToExport.reduce(
        (sum, x) => sum + (x.cantidad ?? 0),
        0
      );
      const lastRow = worksheet.lastRow?.number || rows.length + 6;
      worksheet.mergeCells(`A${lastRow + 1}:F${lastRow + 1}`);
      worksheet.getCell(`A${lastRow + 1}`).value = "Total Acetona:";
      worksheet.getCell(`G${lastRow + 1}`).value = total;
      worksheet.getCell(`G${lastRow + 1}`).numFmt = "#,##0.00";

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), "Reporte_Acetonado.xlsx");
    } catch (error: any) {
      console.error(error);
      notify({ message: `Error al exportar: ${error.message}`, type: "error" });
    }
  };

  const onEditorPreparing = (e: any) => {
    if (e.dataField === "nomina" && e.parentType === "dataRow") {
      e.editorOptions.disabled = e.row?.data?.is365 === true;
    }
  };

  const statusCellRender = (cellData: any) => {
    const s = Number(cellData.value as number | string | undefined);
    const colors: Record<number, string> = {
      [STATUS_PENDIENTE]: "#F8981D",
      [STATUS_CONSOLIDADO]: "#172B4D",
      [STATUS_REPORTADO]: "#0052CC",
      [STATUS_REPORTADO_ALT]: "#0052CC",
      [STATUS_DESCARTADO]: "#6B778C",
    };
    return (
      <span style={{ color: colors[s] || "#172B4D", fontWeight: "bold" }}>
        {getStatusText(s)}
      </span>
    );
  };

  // ====== Reportar TODOS (pendientes del DATASET completo; respeta filtros de barra, no solo la página) ======
  const handleReportAllPending = useCallback(async () => {
    if (viewStatus !== STATUS_PENDIENTE) return;

    // Toma TODO el dataset cargado, filtra solo pendientes
    const pendingIds: string[] = acetylene
      .filter((d) => Number(d.statusAcetileno) === STATUS_PENDIENTE)
      .map((d) => String((d as any).id));

    if (!pendingIds.length) {
      notify({ message: "No hay pendientes para reportar.", type: "info" });
      return;
    }

    const dlg = custom({
      title: "Confirmar acción",
      messageHtml: `<div class='custom-dialog-message'>
        Se reportarán <b>${pendingIds.length}</b> registro(s) pendientes en todas las páginas. ¿Deseas continuar?
      </div>`,
      buttons: [
        {
          text: "Sí, reportar",
          elementAttr: { class: "dx-button-success" },
          onClick: () => true,
        },
        {
          text: "Cancelar",
          elementAttr: { class: "dx-button-danger" },
          onClick: () => false,
        },
      ],
    });

    const confirmed = await dlg.show();
    if (!confirmed) return;

    // Progreso y loading
    setIsReportingAll(true);
    setReportingTotal(pendingIds.length);
    setReportingDone(0);

    const CHUNK_SIZE = 25; // procesa en lotes para no saturar
    const successIds: string[] = [];
    const failedIds: string[] = [];

    try {
      for (let i = 0; i < pendingIds.length; i += CHUNK_SIZE) {
        const batch = pendingIds.slice(i, i + CHUNK_SIZE);

        const results = await Promise.allSettled(
          batch.map((id) => acetilenoService.updateReport(id))
        );

        results.forEach((res, idx) => {
          const id = batch[idx];
          if (res.status === "fulfilled" && !(res.value as any)?.error) {
            successIds.push(id);
          } else {
            failedIds.push(id);
          }
        });

        setReportingDone((prev) => prev + batch.length);
      }

      // Actualiza estado local: marca como reportados los exitosos
      if (successIds.length) {
        const successSet = new Set(successIds);
        setAcetylene((prev) =>
          prev.map((item) =>
            successSet.has(String((item as any).id))
              ? { ...item, statusAcetileno: STATUS_REPORTADO }
              : item
          )
        );
      }

      // Mensajes
      if (successIds.length) {
        notify({
          message: `Reportados ${successIds.length} registro(s).`,
          type: "success",
          displayTime: 2800,
        });
      }
      if (failedIds.length) {
        notify({
          message: `No se pudieron reportar ${failedIds.length} registro(s).`,
          type: "warning",
          displayTime: 3500,
        });
      }

      // regresa a la página 0 tras cambios grandes
      setTimeout(() => dataGridRef.current?.instance()?.pageIndex(0), 0);
    } catch (err: any) {
      notify({ message: err?.message || "Error al reportar.", type: "error" });
    } finally {
      setIsReportingAll(false);
    }
  }, [viewStatus, acetylene]);

  // === Total global de acetona (sobre TODO el dataset filtrado lógico, sin importar paginación del grid) ===
  const totalAcetonaGlobal = useMemo(() => {
    return dataForGrid.reduce((sum, row) => {
      const val = Number(row.cantidad ?? 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);
  }, [dataForGrid]);

  if (loading)
    return (
      <PageContainer
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadIndicator height={60} width={60} visible />
      </PageContainer>
    );

  return (
    <>
      <DevExtremeModalGlobalStyles />
      <PageContainer style={{ position: "relative" }}>
        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 15 }}
        >
          <DxButton
            icon="chevronleft"
            text="Regresar"
            stylingMode="outlined"
            style={{ borderColor: "blue", color: "blue" }}
            onClick={() => navigate(-1)}
          />
        </div>

        <HeaderContainer>
          <Title>Reporte de acetona</Title>

          <ActionsRow>
            <button
              onClick={() => setViewStatus(null)}
              className={viewStatus === null ? "active" : ""}
            >
              Todos
            </button>
            <button
              onClick={() => setViewStatus(STATUS_PENDIENTE)}
              className={viewStatus === STATUS_PENDIENTE ? "active" : ""}
            >
              Pendientes
            </button>
            <button
              onClick={() => setViewStatus(STATUS_REPORTADO)}
              className={viewStatus === STATUS_REPORTADO ? "active" : ""}
            >
              Reportados
            </button>

            {viewStatus === STATUS_PENDIENTE && (
              <AddUserButton
                onClick={handleReportAllPending}
                disabled={isReportingAll}
              >
                {isReportingAll
                  ? `Reportando... ${reportingDone}/${reportingTotal}`
                  : "Reportar Todos"}
              </AddUserButton>
            )}

            <div ref={menuRef} style={{ position: "relative" }}>
              <AddUserButton onClick={() => setShowExportMenu((p) => !p)}>
                Exportar ▼
              </AddUserButton>
              {showExportMenu && (
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "#fff",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.2)",
                    zIndex: 100,
                    right: 0,
                    borderRadius: "4px",
                    border: "1px solid #DFE1E6",
                  }}
                >
                  <div
                    onClick={() => {
                      handleExportExcel();
                      setShowExportMenu(false);
                    }}
                    style={{
                      padding: "10px 18px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    📄 Exportar a Excel
                  </div>
                </div>
              )}
            </div>
          </ActionsRow>
        </HeaderContainer>

        <FilterBar>
          <Field>
            <Label>Sucursal</Label>
            <StyledSelect
              value={filterSucursal}
              onChange={(e) =>
                setFilterSucursal(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
            >
              <option value="">Todas</option>
              {sucursales.map((o) => {
                const id =
                  o.id ?? o.sucursalId ?? o.value ?? o.cc ?? o.numero ?? "";
                const num = o.numero ?? o.cc ?? o.sucursal ?? "";
                const name = o.nombreSuc ?? o.nombre ?? o.name ?? "";
                const label =
                  [num, name].filter(Boolean).join(" - ") || String(id);
                return (
                  <option key={id} value={id}>
                    {label}
                  </option>
                );
              })}
            </StyledSelect>
          </Field>

          <Field>
            <Label>Operador</Label>
            <StyledInput
              placeholder="Ej. jperez"
              value={filterOperador}
              onChange={(e) => setFilterOperador(e.target.value)}
            />
          </Field>

          <Field>
            <Label>Fecha inicio</Label>
            <DateInput
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              max={filterEnd}
            />
          </Field>

          <Field>
            <Label>Fecha fin</Label>
            <DateInput
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              min={filterStart}
            />
          </Field>

          <AddUserButton onClick={handleSearch}>Buscar</AddUserButton>
        </FilterBar>

        <GridContainer>
          <DxDataGrid
            ref={dataGridRef}
            dataSource={dataForGrid}
            keyExpr="id"
            onEditorPreparing={onEditorPreparing}
            wordWrapEnabled
            showBorders
            remoteOperations={false}
            columnAutoWidth
          >
            <SearchPanel
              visible
              highlightCaseSensitive
              placeholder="Buscar..."
            />

            <Column
              caption="#"
              width={60}
              alignment="center"
              allowSorting={false}
              allowFiltering={false}
              cellRender={(e) => {
                const pageIndex = e.component.pageIndex();
                const pageSize = e.component.pageSize();
                const absoluteIndex = pageIndex * pageSize + e.rowIndex + 1;
                return <span>{absoluteIndex}</span>;
              }}
            />

            <Column
              caption="CC - Sucursal"
              calculateCellValue={(row: Acetylene) => {
                const nom = row?.nombreSucursal ?? "";
                const num = row?.numeroSucursal ?? "";
                if (!nom && !num) return "";
                if (!nom) return String(num);
                if (!num) return nom;
                return `${num} - ${nom}`;
              }}
            />

            <Column dataField="nombreUsuario" caption="Operador" />

            <Column
              caption="Fecha y Hora"
              dataField="fechaCompleta"
              dataType="datetime"
              format="dd/MM/yyyy HH:mm:ss"
              calculateCellValue={(data: any) => {
                if (!data?.fecha) return null;
                const iso = `${data.fecha}T${data.hora || "00:00:00"}`;
                const d = new Date(iso);
                return isNaN(d.getTime()) ? null : d;
              }}
            />

            <Column dataField="celda" caption="Báscula" />
            <Column dataField="tipoEnvase" caption="Tipo envase" />
            <Column
              dataField="cantidad"
              caption="Cantidad acetona"
              dataType="number"
              width={150}
              alignment="left"
            />

            <Column
              dataField="statusAcetileno"
              caption="Estatus"
              cellRender={statusCellRender}
            >
              <Lookup
                dataSource={statusFilters}
                valueExpr="value"
                displayExpr="text"
              />
            </Column>

            <Column
              type="buttons"
              caption="Acciones"
              width={110}
              fixed
              fixedPosition="right"
              visible={viewStatus !== STATUS_REPORTADO}
            >
              <Button
                icon={deleteIcon}
                hint="Descartar"
                onClick={(e) => {
                  const rowData: Acetylene = e.row!.data;
                  const dlg = custom({
                    title: "Confirmar acción",
                    messageHtml:
                      "<div class='custom-dialog-message'>¿Estás seguro de que quieres descartar este registro?</div>",
                    buttons: [
                      {
                        text: "Sí, Descartar",
                        elementAttr: { class: "dx-button-success" },
                        onClick: () => true,
                      },
                      {
                        text: "Cancelar",
                        elementAttr: { class: "dx-button-danger" },
                        onClick: () => false,
                      },
                    ],
                  });
                  dlg.show().then(async (result: boolean) => {
                    if (!result) return;
                    try {
                      const response = await acetilenoService.updateDelete(
                        String((rowData as any).id)
                      );
                      if ((response as any)?.error)
                        throw new Error(
                          "No se pudo Descartar el registro."
                        );
                      setAcetylene((prev) =>
                        prev.filter(
                          (item) =>
                            String((item as any).id) !==
                            String((rowData as any).id)
                        )
                      );
                    } catch (err) {
                      console.error(err);
                    }
                  });
                }}
              />
            </Column>

            <FilterRow visible />
            <HeaderFilter visible />

            {/* Paginación visible en tabla */}
            <Paging defaultPageSize={50} />
            <Pager
              visible
              allowedPageSizes={[50]}
              showPageSizeSelector
              showInfo
              showNavigationButtons
            />

            <ColumnFixing enabled />

            {/* ⛔ Ya NO ponemos <Summary> para que no salga el total por página */}
          </DxDataGrid>

          {/* Footer con el total GLOBAL sin importar la página */}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              fontWeight: 600,
              fontSize: 14,
              padding: "8px 12px",
              borderTop: "1px solid #DFE1E6",
              backgroundColor: "#F7F8FA",
            }}
          >
            <div style={{ minWidth: 200, textAlign: "right" }}>
              Total acetona (todas las filas):{" "}
              {totalAcetonaGlobal.toLocaleString("es-MX", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </GridContainer>

        {/* Overlay de carga/progreso para "Reportar Todos" */}
        {isReportingAll && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255,255,255,0.65)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                border: "1px solid #DFE1E6",
                borderRadius: 10,
                padding: 24,
                minWidth: 320,
                boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
                textAlign: "center",
              }}
            >
              <LoadIndicator height={48} width={48} visible />
              <div style={{ marginTop: 12, fontWeight: 600 }}>
                Reportando pendientes...
              </div>
              <div style={{ marginTop: 4 }}>
                {reportingDone}/{reportingTotal}
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  color: "#6B778C",
                }}
              >
                No cierres esta ventana hasta que termine.
              </div>
            </div>
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default AcetilenoList;
